/**
 * The Safe Count entry in the reconciliation area (docs/phases/safe-count.md,
 * section 3): the counts Massiel and Karla tie their weekly 11400 Cash on Hand
 * workpaper to, filterable by location and date range.
 *
 * Same D-7 posture as `Reconciliation.tsx`: the screen renders exactly what
 * `GET /safe-count/reconciliation` answers - a `submitter` gets the router's
 * own 403 message as content, a `reconciliation_viewer` gets the list. And
 * the same money rule: every figure shown, `total` included, is the string
 * the backend returned. Nothing is added up here, across fields or rows.
 */
import { useEffect, useState } from "react";

import {
  LOCATIONS,
  SAFE_COUNT_FIELDS,
  SAFE_COUNT_FIELD_LABELS,
  fetchSafeCountReconciliation,
  formatDetail,
  type Location,
  type Observed,
  type SafeCountRecord,
  type SafeCountReconciliationOut,
} from "../api/public";
import type { TokenGetter } from "../auth/session";
import { ReconciliationNav } from "../components/ReconciliationNav";
import { SessionGate } from "../components/SessionGate";

export function SafeCountReconciliation() {
  return (
    <>
      <ReconciliationNav />
      <SessionGate>{(getToken) => <SafeCountReconciliationView getToken={getToken} />}</SessionGate>
    </>
  );
}

type FetchState =
  | { kind: "loading" }
  /** As in `Reconciliation.tsx`: once a result has landed it stays on screen
   * while a refetch (a changed filter) is in flight. */
  | { kind: "loaded"; result: Observed<SafeCountReconciliationOut>; refetching: boolean };

/** "Petty 150.00 · Coins / Change 37.25 · Drawers 400.00" - the location's
 * fields in its own display order, each exactly as stored. */
function describeCount(row: SafeCountRecord): string {
  return SAFE_COUNT_FIELDS[row.location]
    .map((key) => `${SAFE_COUNT_FIELD_LABELS[key]} ${row.fields[key] ?? "—"}`)
    .join(" · ");
}

function SafeCountReconciliationView({ getToken }: { getToken: TokenGetter }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [location, setLocation] = useState<Location | "">("");
  const [state, setState] = useState<FetchState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => (prev.kind === "loaded" ? { ...prev, refetching: true } : { kind: "loading" }));
    (async () => {
      const token = await getToken();
      const result = await fetchSafeCountReconciliation(
        { dateFrom, dateTo, location: location || undefined },
        token,
      );
      if (!cancelled) setState({ kind: "loaded", result, refetching: false });
    })();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, location, getToken]);

  if (state.kind === "loading") {
    return <p className="muted">Loading safe counts…</p>;
  }

  const { result, refetching } = state;
  const detail =
    result.body && typeof result.body === "object" && "detail" in result.body
      ? formatDetail(result.body.detail)
      : "";

  if (result.status === 403) {
    // The ordinary answer for a submitter; the server's message names the
    // committed access file, so it is shown as-is.
    return (
      <section className="card" data-testid="safe-count-reconciliation-refused">
        <h2>Safe Counts</h2>
        <p>{detail || "You do not have access to the reconciliation view."}</p>
      </section>
    );
  }

  if (!result.ok || !result.body || !("rows" in result.body)) {
    return (
      <section className="card error" data-testid="safe-count-reconciliation-error">
        <h2>Safe Counts</h2>
        <p>{detail || `Request failed (${result.status}).`}</p>
      </section>
    );
  }

  const data: SafeCountReconciliationOut = result.body;

  return (
    <>
      <section className="card">
        <h2>Safe Counts</h2>
        <div className="filters">
          <label className="field">
            From
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label className="field">
            To
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
          <label className="field">
            Location
            <select value={location} onChange={(e) => setLocation(e.target.value as Location | "")}>
              <option value="">All locations</option>
              {LOCATIONS.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </label>
        </div>
        {refetching && <p className="muted">Refreshing…</p>}
      </section>

      <section className="card" data-testid="safe-count-reconciliation-table">
        {data.rows.length === 0 ? (
          <p className="muted">No safe counts in this range.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>As of</th>
                  <th>Location</th>
                  <th>Count</th>
                  <th>Total</th>
                  <th>Submitted by</th>
                  <th>Photos</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.safe_count_as_of}</td>
                    <td>{row.location}</td>
                    <td>{describeCount(row)}</td>
                    <td>{row.total}</td>
                    <td>{row.submitted_by}</td>
                    <td>{row.attachment_ids.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
